import type { ReactNode } from 'react';

import { useNarrow } from '../../../lib/use-narrow';
import { Button, Typography } from '../ui';
import styles from './EmptyIntro.module.css';

/**
 * The shared "wake-up" empty state — Figma `5773:9154`, instanced by both frames on the
 * Empty States page (`10941:74616`): an illustration, the display-size title, three
 * numbered steps, and the black CTA that hands off to the Maestro.
 *
 * The same block ships on native as `HomeEmptyState` / `AgentsEmptyIntro`
 * (`android/.../features/Pages.kt`, `ios/.../SymphonyHomeView.swift`) — the steps run in a
 * row here because the desktop frame has the width for it, and stack below 720px like
 * native does. The desktop and phone frames run the block at DIFFERENT steps (gaps, step
 * measures, CTA size); the table at the top of the stylesheet is the reference for which
 * is which.
 */
export function EmptyIntro({
  illustration,
  title,
  steps,
  ctaLabel,
  onCta,
}: {
  illustration: ReactNode;
  title: string;
  /** Exactly the three steps of the design; numbered 1..3 in order. */
  steps: [string, string, string];
  ctaLabel: string;
  onCta: () => void;
}) {
  // The phone frame draws the CTA full width at Large (42px, `15377:97719`); the desktop
  // frame auto-width at Medium (36px, 164 wide — `15378:165667`). Taken off the DS size
  // rather than restyled here, so the button keeps its own metrics (`test/button-spec`).
  // `useNarrow` is false for the first paint, where the stylesheet's `stretch` already
  // gives the CTA the full width — only its height settles a frame later.
  const narrow = useNarrow();

  return (
    <div className={styles.root}>
      <div className={styles.illustration}>{illustration}</div>
      <div className={styles.copy}>
        <Typography variant="h1" component="h1" color="strong" className={styles.title}>
          {title}
        </Typography>
        <ol className={styles.steps}>
          {steps.map((step, i) => (
            <li key={step} className={styles.step}>
              {/* The <ol> already conveys the order, so the numeral is decorative;
                  `color="inherit"` leaves the light-on-black to the badge itself. */}
              <Typography variant="p3" component="span" color="inherit" className={styles.badge} aria-hidden>
                {i + 1}
              </Typography>
              <Typography variant="p2" component="span" color="strong" className={styles.stepText}>
                {step}
              </Typography>
            </li>
          ))}
        </ol>
        <Button size={narrow ? 'large' : 'medium'} fullWidth={narrow} onClick={onCta}>
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
}
